import React from "react";
import { Icon } from "@iconify/react";

const Pagination = ({ page, totalPages, setPage }) => {
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);

      let start = Math.max(2, page - 1);
      let end = Math.min(totalPages - 1, page + 1);

      if (page <= 2) {
        end = 3;
      }
      if (page >= totalPages - 1) {
        start = totalPages - 2;
      }

      if (start > 2) {
        pages.push("...");
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      if (end < totalPages - 1) {
        pages.push("...");
      }

      pages.push(totalPages);
    }
    return pages;
  };

  if (totalPages <= 1) return null;

  return (
    <div className="flex items-center justify-between bg-white px-5 py-4 mt-6 rounded-2xl border border-slate-100 shadow-sm select-none">
      {/* Mobile view */}
      <div className="flex flex-1 justify-between sm:hidden">
        <button
          disabled={page <= 1}
          onClick={() => setPage(page - 1)}
          className="relative inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-4 py-2 text-xs font-black text-slate-600 hover:bg-slate-50 active:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
        >
          <Icon icon="solar:arrow-left-outline" className="w-4 h-4" />
          <span>Previous</span>
        </button>
        <span className="text-xs font-bold text-slate-500 self-center">
          Page <span className="text-slate-800 font-extrabold">{page}</span> of <span className="text-slate-800 font-extrabold">{totalPages}</span>
        </span>
        <button
          disabled={page >= totalPages}
          onClick={() => setPage(page + 1)}
          className="relative inline-flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-4 py-2 text-xs font-black text-slate-600 hover:bg-slate-50 active:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
        >
          <span>Next</span>
          <Icon icon="solar:arrow-right-outline" className="w-4 h-4" />
        </button>
      </div>

      {/* Desktop view */}
      <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
        <div>
          <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">
            Showing Page <span className="text-slate-800 font-black">{page}</span> of{" "}
            <span className="text-slate-800 font-black">{totalPages}</span>
          </p>
        </div>
        <div>
          <nav className="inline-flex items-center gap-1.5" aria-label="Pagination">
            {/* Previous */}
            <button
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
              className="w-9 h-9 flex items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-550 hover:border-slate-350 hover:bg-slate-50 active:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-all cursor-pointer"
              title="Previous Page"
            >
              <Icon icon="solar:arrow-left-outline" className="w-4 h-4" />
            </button>
            
            {/* Page Numbers */}
            {getPageNumbers().map((p, idx) => (
              <button
                key={idx}
                disabled={p === "..."}
                onClick={() => p !== "..." && setPage(p)}
                className={`w-9 h-9 flex items-center justify-center rounded-xl text-xs font-extrabold transition-all duration-300 ${
                  p === page
                    ? "bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-md shadow-cyan-500/15 cursor-default"
                    : p === "..."
                    ? "text-slate-400 cursor-default"
                    : "border border-slate-200 bg-white text-slate-650 hover:border-slate-350 hover:bg-slate-50 active:bg-slate-100 cursor-pointer"
                }`}
              >
                {p}
              </button>
            ))}

            {/* Next */}
            <button
              disabled={page >= totalPages}
              onClick={() => setPage(page + 1)}
              className="w-9 h-9 flex items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-550 hover:border-slate-350 hover:bg-slate-50 active:bg-slate-100 disabled:opacity-40 disabled:cursor-not-allowed transition-all cursor-pointer"
              title="Next Page"
            >
              <Icon icon="solar:arrow-right-outline" className="w-4 h-4" />
            </button>
          </nav>
        </div>
      </div>
    </div>
  );
};

export default Pagination;
